import { call, put, race, take, takeLatest } from "redux-saga/effects"
import {
  CHECKUP_DOCUMENT_REQUEST,
  CUSTOMER_DOCUMENT_PREVIEW_CLEAR,
  CUSTOMER_DOCUMENT_PREVIEW_REQUEST,
  CUSTOMER_DOCUMENTS_REQUEST,
  DOCUMENTS_REQUEST,
  SEND_CHECKUP_DOCUMENTS_REQUEST,
  SEND_DOCUMENTS_REQUEST,
} from "./constants"
import { actions } from "./actions"
import { CustomerDocument, DocumentType } from "./types"
import config from "react-global-configuration"

import { request } from "lib/request"
import { Action, GWatcher } from "types/redux"
import { getMergedBlob } from "lib/pdf-merger"
import { DOCUMENT_NOT_FOUND_PAGE } from "core/constants"
import { languages } from "locales/languages"



const apiSendDocuments = async ({ id }: { id: number }) => {
  return await request(`${config.get("admin.sendDocuments")}/${id}`, {
    method: "POST",
  })
}

const apiSendCheckupDocuments = async ({ relativeId }: { relativeId: number | null }) => {
  let url = config.get("admin.sendCheckupDocuments")
  if (relativeId) {
    url += `?relative_id=${relativeId}`
  }
  return await request(url, {
    method: "POST",
  })
}

const getCheckupDocument = async (payload: {
  relative_id: number | null
  vitals: Record<string, unknown>
}): Promise<Blob | string | { url?: string }> => {
  return await request<Blob | string | { url?: string }>(`${config.get("admin.checkup")}`, {
    method: "POST",
    payload,
    responseFormat: "blob",
  })
}

const getUrlDocument = (documentType: DocumentType) => {
  switch (documentType) {
    case DocumentType.REPORT:
      return config.get("patient.get.document.report")
    case DocumentType.WORKLEAVE:
      return config.get("patient.get.document.workleave")
    case DocumentType.CERTIFICATE:
      return config.get("patient.get.document.certificate")
    case DocumentType.PRESCRIPTION:
    default:
      return config.get("patient.get.document.prescription")
  }
}

async function getDocumentBlobApi(
  callId: string,
  documentUrl: string,
): Promise<Blob> {
  return await request<Blob>(`${documentUrl}/${callId}`, {
    // @TODO APIside : Change POST to GET
    method: "POST",
    responseFormat: "blob",
  })
}

const DOCUMENT_DOWNLOAD_MAX_RETRY = 4

async function getMergedDocumentsBlobApi(
  callId: string,
  consultationType: "calls" | "teleexpertises" | "consultations" = "calls",
  retryNumber = 0,
) {
  try {
    const res: any = await request<Blob>(
      `/${consultationType}/documents/${callId}`,
      {
        method: "GET",
        responseFormat: "blob",
      },
    )
    if (res?.code && res?.code !== 200) {
      if (retryNumber >= DOCUMENT_DOWNLOAD_MAX_RETRY) {
        console.error("Max retries reached for document fetch", res.code)
        return undefined
      }
      console.warn(
        `Retrying document fetch... attempt ${retryNumber + 1}`,
        res.code,
      )
      await new Promise((resolve) => setTimeout(resolve, 1000))
      return getMergedDocumentsBlobApi(
        callId,
        consultationType,
        retryNumber + 1,
      )
    }
    return res // success → return blob
  } catch (err: any) {
    // Handle server errors (thrown from handleApiErrors)
    if (retryNumber >= DOCUMENT_DOWNLOAD_MAX_RETRY) {
      console.error("Max retries reached for document fetch", err)
      return undefined
    }
    console.warn(`Retrying document fetch... attempt ${retryNumber + 1}`, err)
    await new Promise((resolve) => setTimeout(resolve, 1000))
    return getMergedDocumentsBlobApi(callId, consultationType, retryNumber + 1)
  }
}

function* sendDocumentsFlow(action: Action<{ id: number }>) {
  try {
    yield call(apiSendDocuments, action.payload)
    yield put(
      actions.sendDocumentsSuccess("Les documents ont bien été envoyés !"),
    )
  } catch (e) {
    console.error(e, {
      route: `${config.get("admin.sendDocuments")}/${action.payload?.id}`,
    })
    yield put(
      actions.sendDocumentsError("Les documents n'ont pas pu être envoyé"),
    )
  }
}

function* sendCheckupDocumentsFlow(action: Action<{ relativeId: number | null }>) {
  try {
    yield call(apiSendCheckupDocuments, { relativeId: action.payload.relativeId })
    yield put(actions.sendCheckupDocumentsSuccess(languages.checkupDocumentSentSuccess))
  } catch (e) {
    console.error(e, {
      route: `${config.get("admin.sendCheckupDocuments")}?relative_id=${action.payload?.relativeId || ""}`,
    })
    yield put(actions.sendCheckupDocumentsError(languages.checkupDocumentSentError))
  }
}


function* checkupDocumentFlow(
  action: Action<{
    relative_id: number | null
    vitals: Record<string, unknown>
  }>,
): any {
  try {
    const response: Blob | string | { url?: string } = yield call(
      getCheckupDocument,
      action.payload,
    )
    const documentUrl =
      response instanceof Blob
        ? window.URL.createObjectURL(response)
        : typeof response === "string"
          ? response
          : response?.url

    if (!documentUrl) {
      throw new Error("Checkup document response does not contain a usable URL")
    }

    yield put(actions.checkupDocumentSuccess(documentUrl))
  } catch (e) {
    console.error(e, {
      route: `${config.get("admin.checkup")}`,
    })
    yield put(actions.checkupDocumentError(languages.checkupDocumentError))
  }
}

async function getMergedDocumentsBlob(
  callId: string,
  documentsType: DocumentType[],
) {
  const promises = documentsType.map((document) => {
    const id = callId
    const documentUrl = getUrlDocument(document)
    return getDocumentBlobApi(id, documentUrl)
  })
  const blobs: Blob[] = await Promise.all(promises)

  return await getMergedBlob(blobs)
}

function* documentsFlow(
  action: Action<{
    documentsType: DocumentType[]
    callId: string
    consultationType: "calls" | "teleexpertises" | "consultations"
  }>,
): any {
  const { documentsType, callId, consultationType = "calls" } = action.payload
  try {
    const blob = yield documentsType
      ? getMergedDocumentsBlob(callId, documentsType)
      : getMergedDocumentsBlobApi(callId, consultationType)
    let documentUrl = DOCUMENT_NOT_FOUND_PAGE
    if (blob) {
      documentUrl = window.URL.createObjectURL(blob)
    }

    yield put(actions.success(documentUrl))
  } catch (e) {
    console.error(e, {
      route: "documentsFlow::createObjectURL",
    })
    yield put(actions.error(e as string))
  }
}

// Documents imported by the patient (Profile > Documents importés). Same
// endpoint as the survey document selection, but the list is not filtered by
// patient: the account holder and the relatives are shown together.
const getCustomerDocuments = async (): Promise<CustomerDocument[]> =>
  await request<CustomerDocument[]>(config.get("patient.uploadedDocuments"), {
    method: "GET",
  })

const getCustomerDocumentBlob = async (url: string): Promise<Blob> =>
  await request<Blob>(url, { method: "GET", responseFormat: "blob" })

function* customerDocumentsFlow(): any {
  try {
    const documents: CustomerDocument[] = yield call(getCustomerDocuments)
    yield put(
      actions.customerDocumentsSuccess(Array.isArray(documents) ? documents : []),
    )
  } catch (e) {
    console.error(e, { route: config.get("patient.uploadedDocuments") })
    yield put(actions.customerDocumentsError(languages.importedDocumentsError))
  }
}

function* customerDocumentPreviewFlow(action: Action<CustomerDocument>): any {
  const { url } = action.payload
  try {
    // Closing the preview (or leaving the page) while the file is loading
    // drops the response: no late reopening, no object URL left behind.
    const { blob, cleared }: { blob?: Blob; cleared?: unknown } = yield race({
      blob: call(getCustomerDocumentBlob, url),
      cleared: take(CUSTOMER_DOCUMENT_PREVIEW_CLEAR),
    })
    if (cleared) return
    if (!(blob instanceof Blob)) {
      throw new Error("Customer document response is not a file")
    }
    yield put(
      actions.customerDocumentPreviewSuccess(window.URL.createObjectURL(blob)),
    )
  } catch (e) {
    console.error(e, { route: url })
    yield put(
      actions.customerDocumentPreviewError(languages.importedDocumentPreviewError),
    )
  }
}

function* documentsWatcher(): GWatcher {
  yield takeLatest(DOCUMENTS_REQUEST, documentsFlow)
  yield takeLatest(CHECKUP_DOCUMENT_REQUEST, checkupDocumentFlow)
  yield takeLatest(SEND_DOCUMENTS_REQUEST, sendDocumentsFlow)
  yield takeLatest(SEND_CHECKUP_DOCUMENTS_REQUEST, sendCheckupDocumentsFlow)
  yield takeLatest(CUSTOMER_DOCUMENTS_REQUEST, customerDocumentsFlow)
  yield takeLatest(CUSTOMER_DOCUMENT_PREVIEW_REQUEST, customerDocumentPreviewFlow)
}

export default documentsWatcher
